import React from 'react';
import { Button } from '@mui/material';
import { Row } from 'ui/Flex';
import { MuiModal } from 'ui/MuiModal';
import {
    ACTION_TYPES_WITHOUT_LOCATION,
    getActionTypeOptions,
    LOCATION_INDEPENDENT_ACTION_TYPES
} from 'components/actions/utils';
import { CheckboxPlainPicker } from 'lib/checkbox-picker/CheckboxPlainPicker';
import { ActionType } from 'components/actions/models/Action';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';

interface IActionTypesDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (selectedOptions: string[]) => void;
    selectedOptions: string[];
    hasLocationsSelected?: boolean;
}

const ActionTypesDialogContent: React.FC<Omit<IActionTypesDialogProps, 'open'>> = ({
    onSubmit,
    onClose,
    selectedOptions,
    hasLocationsSelected
}) => {
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const [selectedActionTypeValues, setSelectedActionTypeValues] = React.useState<string[]>(selectedOptions);

    const finalActionTypeOptions = React.useMemo(() => {
        const actionTypeOptions = getActionTypeOptions({
            includeNoneOption: false,
            subscriptionsEnabled: settings?.subscriptionsEnabled
        });
        return actionTypeOptions.filter(option => {
            // here the type assertion: getActionTypeOptions returns { value: string } but values are guaranteed
            // to be ActionType enum values based on function implementation in "components/actions/utils"
            const actionType: ActionType = option.value as ActionType;
            return !(
                ((!hasLocationsSelected && !ACTION_TYPES_WITHOUT_LOCATION.includes(actionType)) ||
                    (hasLocationsSelected && ACTION_TYPES_WITHOUT_LOCATION.includes(actionType))) &&
                !LOCATION_INDEPENDENT_ACTION_TYPES.includes(actionType)
            );
        });
    }, [hasLocationsSelected, settings?.subscriptionsEnabled]);

    const handleItemToggle = React.useCallback((values: string[]) => {
        setSelectedActionTypeValues(values);
    }, []);

    const handleSubmit = React.useCallback(() => {
        onSubmit(selectedActionTypeValues);
    }, [onSubmit, selectedActionTypeValues]);

    return (
        <>
            <CheckboxPlainPicker
                options={finalActionTypeOptions}
                value={selectedActionTypeValues}
                searchPlaceholder="Search Action Type"
                onChange={handleItemToggle}
                disableSelectAll
            />
            <Row flex={1} align="flex-end">
                <Button onClick={onClose}>Cancel</Button>
                <Button color="primary" onClick={handleSubmit}>
                    Confirm
                </Button>
            </Row>
        </>
    );
};

export const ActionTypesDialog: React.FC<IActionTypesDialogProps> = props => {
    const { open, onClose } = props;
    return (
        <MuiModal open={open} onClose={onClose}>
            <ActionTypesDialogContent {...props} />
        </MuiModal>
    );
};
